import React, { useState, useRef } from 'react';
import { X, Wand2, Loader2, Upload, Link as LinkIcon, FileAudio, FileVideo, CheckCircle2, Download, Mic, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import CoinDisplay from './CoinDisplay';

const TranscribeModal = ({ isOpen, onClose, onTranscribe }) => {
    const [tab, setTab] = useState('url'); // 'url' or 'upload'
    const [url, setUrl] = useState('');
    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingStep, setLoadingStep] = useState(0); // 0: idle, 1: validating, 2: downloading, 3: transcribing, 4: done
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    if (!isOpen) return null;

    const loadingSteps = [
        { id: 1, label: 'Mengesahkan URL...', icon: LinkIcon },
        { id: 2, label: 'Memuat turun video...', icon: Download },
        { id: 3, label: 'Mentranskrip audio...', icon: Mic },
        { id: 4, label: 'Selesai!', icon: CheckCircle2 }
    ];

    const handleSubmit = async () => {
        setIsLoading(true);
        setError(null);
        setLoadingStep(1);

        try {
            // Simulate step progression for better UX
            if (tab === 'url') {
                // Step 1: Validate
                await new Promise(r => setTimeout(r, 500));
                setLoadingStep(2);

                // Step 2 & 3: Download and Transcribe (happens in Cloud Function)
                await new Promise(r => setTimeout(r, 1000));
                setLoadingStep(3);

                await onTranscribe({ type: 'url', value: url });
            } else {
                setLoadingStep(3); // Skip to transcribing for file uploads
                await onTranscribe({ type: 'file', value: file });
            }

            setLoadingStep(4);
            await new Promise(r => setTimeout(r, 500));

            // Reset state after completion
            setUrl('');
            setFile(null);
            setLoadingStep(0);
        } catch (err) {
            setError(err.message || 'Transcription gagal. Sila cuba lagi.');
            setLoadingStep(0);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            // Check file size (25MB max)
            if (selectedFile.size > 25 * 1024 * 1024) {
                setError('Fail terlalu besar. Maksimum 25MB.');
                return;
            }
            setFile(selectedFile);
            setError(null);
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    // Check if URL is a short URL
    const isShortUrl = url.includes('vm.tiktok.com') || url.includes('vt.tiktok.com');

    return (
        <AnimatePresence>
            <div className="modal-overlay" onClick={isLoading ? undefined : onClose}>
                <motion.div
                    className="modal-content"
                    onClick={e => e.stopPropagation()}
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    style={{ maxWidth: '480px' }}
                >
                    <div className="modal-header">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2>Transcription Baru</h2>
                            <X size={24} color="#64748b" cursor="pointer" onClick={isLoading ? undefined : onClose} />
                        </div>
                        <p style={{ color: '#64748b' }}>Masukkan link TikTok atau muat naik fail audio/video.</p>
                    </div>

                    {/* Coin Display */}
                    <div style={{ marginBottom: '16px' }}>
                        <CoinDisplay compact />
                    </div>

                    {/* Error Alert */}
                    {error && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px',
                            padding: '12px',
                            background: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '12px',
                            marginBottom: '16px',
                            color: '#dc2626'
                        }}>
                            <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '1px' }} />
                            <p style={{ fontSize: '13px', margin: 0 }}>{error}</p>
                        </div>
                    )}

                    {/* Loading Progress */}
                    {isLoading && loadingStep > 0 && (
                        <div style={{
                            padding: '16px',
                            background: '#f8fafc',
                            borderRadius: '12px',
                            marginBottom: '16px'
                        }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {loadingSteps.map((step) => {
                                    const isActive = loadingStep === step.id;
                                    const isComplete = loadingStep > step.id;
                                    const isPending = loadingStep < step.id;
                                    const Icon = step.icon;

                                    return (
                                        <div
                                            key={step.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                opacity: isPending ? 0.4 : 1,
                                                transition: 'opacity 0.3s'
                                            }}
                                        >
                                            <div style={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: isComplete ? '#10b981' : isActive ? '#6366f1' : '#e2e8f0',
                                                transition: 'background 0.3s'
                                            }}>
                                                {isComplete ? (
                                                    <CheckCircle2 size={16} color="white" />
                                                ) : isActive ? (
                                                    <Loader2 size={16} color="white" className="animate-spin" />
                                                ) : (
                                                    <Icon size={16} color="#94a3b8" />
                                                )}
                                            </div>
                                            <span style={{
                                                fontSize: '13px',
                                                fontWeight: isActive ? 600 : 400,
                                                color: isActive ? '#1e293b' : '#64748b'
                                            }}>
                                                {step.label}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Tabs - Hidden during loading */}
                    {!isLoading && (
                        <>
                            <div style={{
                                display: 'flex',
                                gap: '8px',
                                background: '#f1f5f9',
                                padding: '4px',
                                borderRadius: '10px',
                                marginBottom: '24px'
                            }}>
                                <button
                                    onClick={() => { setTab('url'); setError(null); }}
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        background: tab === 'url' ? 'white' : 'transparent',
                                        boxShadow: tab === 'url' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        color: tab === 'url' ? '#2563eb' : '#64748b'
                                    }}
                                >
                                    <LinkIcon size={16} /> Link TikTok
                                </button>
                                <button
                                    onClick={() => { setTab('upload'); setError(null); }}
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        background: tab === 'upload' ? 'white' : 'transparent',
                                        boxShadow: tab === 'upload' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        color: tab === 'upload' ? '#2563eb' : '#64748b'
                                    }}
                                >
                                    <Upload size={16} /> Muat Naik
                                </button>
                            </div>

                            <div className="input-group">
                                {tab === 'url' ? (
                                    <>
                                        <label>URL Video TikTok</label>
                                        <input
                                            type="text"
                                            className="input-field"
                                            placeholder="https://www.tiktok.com/@user/video/... atau vm.tiktok.com/..."
                                            value={url}
                                            onChange={(e) => { setUrl(e.target.value); setError(null); }}
                                            disabled={isLoading}
                                        />
                                        {isShortUrl && (
                                            <p style={{ fontSize: '11px', color: '#10b981', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <CheckCircle2 size={12} /> Short URL akan di-expand automatik
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <label>Fail Audio atau Video</label>
                                        <div
                                            onClick={isLoading ? undefined : triggerFileInput}
                                            style={{
                                                border: '2px dashed #cbd5e1',
                                                borderRadius: '12px',
                                                padding: '32px',
                                                textAlign: 'center',
                                                cursor: isLoading ? 'default' : 'pointer',
                                                background: file ? '#f0f9ff' : 'transparent',
                                                borderColor: file ? '#2563eb' : '#cbd5e1',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <input
                                                type="file"
                                                hidden
                                                ref={fileInputRef}
                                                onChange={handleFileChange}
                                                accept="audio/mp3,audio/wav,audio/mpeg,video/mp4"
                                            />
                                            {file ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                    {file.type.startsWith('audio') ? <FileAudio size={40} color="#2563eb" /> : <FileVideo size={40} color="#2563eb" />}
                                                    <span style={{ fontWeight: 600 }}>{file.name}</span>
                                                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                    <Upload size={40} color="#94a3b8" />
                                                    <span style={{ color: '#64748b' }}>Klik untuk muat naik MP3 atau Video</span>
                                                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Saiz maksimum: 25MB</span>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </>
                    )}

                    <div className="modal-actions">
                        {!isLoading && <button className="btn-outline" onClick={onClose}>Batal</button>}
                        <button
                            className="btn-primary"
                            onClick={handleSubmit}
                            disabled={isLoading || (tab === 'url' ? !url : !file)}
                            style={{ opacity: (isLoading || (tab === 'url' ? !url : !file)) ? 0.6 : 1, minWidth: '160px', justifyContent: 'center' }}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="animate-spin" size={18} />
                                    <span>Memproses...</span>
                                </>
                            ) : (
                                <>
                                    <Wand2 size={18} />
                                    <span>Mula Transcribe</span>
                                </>
                            )}
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default TranscribeModal;
